import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Link2, Loader2, Trash2, Power, Settings, Gauge, TrendingUp, Shield,
  Eye, EyeOff, CheckCircle2, AlertCircle, Clock, Plus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function apiErrorDetail(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || String(item)).join(". ");
  }
  return err?.response?.data?.message || fallback;
}

function statusBadge(s) {
  const m = {
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    verified: "bg-[#00E676]/10 text-[#00E676] border-[#00E676]/30",
    failed: "bg-[#FF3D00]/10 text-[#FF3D00] border-[#FF3D00]/30",
  };
  const icons = {
    pending: <Clock className="w-3 h-3" />,
    verified: <CheckCircle2 className="w-3 h-3" />,
    failed: <AlertCircle className="w-3 h-3" />,
  };
  const labels = { pending: "Pending", verified: "Verified", failed: "Failed" };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${m[s] || m.pending}`}>
      {icons[s] || icons.pending} {labels[s] || "Pending"}
    </span>
  );
}

function AccountSettings({ account, planLimits, totalAllocated, onSave }) {
  const [settings, setSettings] = useState(account.settings || {});

  useEffect(() => {
    setSettings(account.settings || {});
  }, [account.id, account.settings]);

  const save = (updates) => onSave(account.id, updates);

  const refBal = Math.max(account.balance || 0, account.equity || 0);
  const allocType = settings.allocation_type || "percentage";
  const allocVal = settings.allocation_value ?? 100;
  const rawAlloc = allocType === "fixed"
    ? Math.min(allocVal, refBal || allocVal)
    : refBal * (Math.min(allocVal, 100) / 100);
  const planCap = planLimits?.max_total_capital ?? Infinity;
  const computedAlloc = refBal > 0 ? Math.min(rawAlloc, refBal, planCap) : 0;
  const allocated = account.allocated_capital ?? computedAlloc;
  const isCapped = refBal > 0 && rawAlloc > allocated + 0.01;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3">
          <p className="text-[10px] text-[#52525B] uppercase tracking-wider">Balance</p>
          <p className="text-base font-bold font-data text-[#00E676]">
            ${account.balance?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "—"}
          </p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3">
          <p className="text-[10px] text-[#52525B] uppercase tracking-wider">Equity</p>
          <p className="text-base font-bold font-data text-[#007AFF]">
            ${account.equity?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "—"}
          </p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 col-span-2 sm:col-span-1">
          <p className="text-[10px] text-[#52525B] uppercase tracking-wider">Allocated</p>
          <p className="text-base font-bold font-data text-[#FFC107]">${allocated.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 col-span-2 sm:col-span-1">
          <p className="text-[10px] text-[#52525B] uppercase tracking-wider">Plan Limit</p>
          <p className="text-base font-bold font-data text-white">
            ${Number(planLimits?.max_total_capital || 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div>
        <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2.5">Strategy</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "conservative", label: "Conservative", desc: "0.5% risk", color: "#007AFF", icon: <Shield className="w-4 h-4" /> },
            { id: "balanced", label: "Balanced", desc: "1% risk", color: "#00E676", icon: <Gauge className="w-4 h-4" /> },
            { id: "aggressive", label: "Aggressive", desc: "2% risk", color: "#FF6D00", icon: <TrendingUp className="w-4 h-4" /> },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setSettings((p) => ({ ...p, strategy: s.id })); save({ strategy: s.id }); }}
              className={`p-3 rounded-xl border text-center transition-all ${settings.strategy === s.id ? "border-transparent ring-1" : "border-[#27272A] bg-[#0A0A0A] hover:border-[#52525B]"}`}
              style={settings.strategy === s.id ? { borderColor: s.color, backgroundColor: `${s.color}12`, boxShadow: `0 0 0 1px ${s.color}50` } : {}}
            >
              <div className="flex justify-center mb-1" style={{ color: settings.strategy === s.id ? s.color : "#52525B" }}>{s.icon}</div>
              <p className="text-[11px] font-semibold">{s.label}</p>
              <p className="text-[9px] text-[#52525B]">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2.5">Capital Allocation</p>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 space-y-3">
          <div className="grid grid-cols-2 gap-1.5">
            {[{ id: "percentage", label: "% of Balance" }, { id: "fixed", label: "Fixed Amount" }].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  const v = opt.id === "percentage" ? 100 : Math.min(refBal || 1000, planLimits?.max_total_capital || refBal);
                  setSettings((p) => ({ ...p, allocation_type: opt.id, allocation_value: v }));
                  save({ allocation_type: opt.id, allocation_value: v });
                }}
                className={`py-1.5 rounded-lg text-[11px] font-medium transition-all ${(settings.allocation_type || "percentage") === opt.id ? "bg-[#007AFF] text-white" : "bg-[#1C1C1E] text-[#A1A1AA]"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {(settings.allocation_type || "percentage") === "fixed" && <span className="text-[#52525B] text-sm">$</span>}
            <Input
              type="number"
              step={(settings.allocation_type || "percentage") === "percentage" ? "5" : "100"}
              min={(settings.allocation_type || "percentage") === "percentage" ? "10" : "1"}
              max={(settings.allocation_type || "percentage") === "percentage" ? "100" : undefined}
              value={settings.allocation_value ?? 100}
              onChange={(e) => setSettings({ ...settings, allocation_value: parseFloat(e.target.value) || 0 })}
              onBlur={() => save({ allocation_value: settings.allocation_value })}
              className="bg-[#1C1C1E] border-[#27272A] text-white h-8 flex-1 text-xs"
            />
            {(settings.allocation_type || "percentage") === "percentage" && <span className="text-[#52525B] text-sm">%</span>}
          </div>
          {refBal > 0 ? (
            <p className="text-[10px] text-[#52525B]">
              Effective allocation: <span className="text-[#007AFF] font-semibold">${allocated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {isCapped && (
                <span className="text-[#FFC107]"> (capped by plan limit)</span>
              )}
            </p>
          ) : (
            <p className="text-[10px] text-[#52525B]">
              Balance not synced yet — run executor sync to refresh equity before allocation applies.
            </p>
          )}
        </div>
      </div>

      <div>
        <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2.5">Risk Parameters</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { key: "risk_per_trade", label: "Risk / Trade", suffix: "%", step: "0.1", min: "0.1", max: "100" },
            { key: "max_risk_per_day", label: "Max Risk / Day", suffix: "%", step: "0.5", min: "0.5", max: "100" },
            { key: "daily_profit_target", label: "Profit Target / Day", suffix: "%", step: "0.5", min: "0.1", max: "1000" },
            { key: "max_open_trades", label: "Max Open Trades", suffix: "", step: "1", min: "1", max: "100", isInt: true },
          ].map((p) => (
            <div key={p.key}>
              <Label className="text-[10px] text-[#52525B]">{p.label}</Label>
              <div className="flex items-center gap-1.5 mt-1">
                <Input
                  type="number"
                  step={p.step}
                  min={p.min}
                  max={p.max}
                  value={settings[p.key]}
                  onChange={(e) => setSettings({
                    ...settings,
                    [p.key]: p.isInt ? (parseInt(e.target.value, 10) || 0) : (parseFloat(e.target.value) || 0),
                  })}
                  onBlur={() => save({ [p.key]: settings[p.key] })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white h-8 text-xs flex-1"
                />
                {p.suffix && <span className="text-[#52525B] text-xs">{p.suffix}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Mt5AccountManager({ getHeaders, onAccountsChange }) {
  const [accounts, setAccounts] = useState([]);
  const [planLimits, setPlanLimits] = useState(null);
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [canAddAccount, setCanAddAccount] = useState(false);
  const [planMeta, setPlanMeta] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mt5Form, setMt5Form] = useState({ account_number: "", password: "", server: "" });
  const [linking, setLinking] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/user/mt5-accounts`, { headers: getHeaders() });
      const list = res.data.accounts || [];
      setAccounts(list);
      setPlanLimits(res.data.plan_limits);
      setPlanMeta(res.data.plan_meta || null);
      setTotalAllocated(res.data.total_allocated || 0);
      setCanAddAccount(res.data.can_add_account);
      setSelectedId((prev) => {
        if (prev && list.some((a) => a.id === prev)) return prev;
        return list[0]?.id || null;
      });
      onAccountsChange?.(list, res.data);
    } catch (err) {
      console.error('Failed to load MT5 accounts', err);
      const status = err?.response?.status;
      if (status === 500) {
        console.warn('Server error loading accounts — may still be initializing');
      } else if (status !== 401) {
        toast.error(apiErrorDetail(err, 'Failed to load MT5 accounts'));
      }
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pending = accounts.some((a) => a.verification_status === "pending");
    if (!pending) return;
    const interval = setInterval(fetchAccounts, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.map((a) => a.verification_status).join(",")]);

  const selected = accounts.find((a) => a.id === selectedId);

  const handleLink = async (e) => {
    e.preventDefault();
    const accountNumber = mt5Form.account_number.trim();
    if (!/^\d+$/.test(accountNumber)) {
      toast.error("Enter a valid numeric MT5 account number");
      return;
    }
    if (!mt5Form.password.trim() || !mt5Form.server.trim()) {
      toast.error("Password and server are required");
      return;
    }
    setLinking(true);
    try {
      const res = await axios.post(`${API}/user/mt5-account`, {
        account_number: accountNumber,
        password: mt5Form.password,
        server: mt5Form.server.trim(),
      }, { headers: getHeaders() });
      toast.success(res.data?.message || "MT5 account added! Verification pending.");
      setMt5Form({ account_number: "", password: "", server: "" });
      setShowAddForm(false);
      await fetchAccounts();
    } catch (err) {
      toast.error(apiErrorDetail(err, "Failed to link MT5 account"));
    } finally {
      setLinking(false);
    }
  };

  const handleRemove = async (accountId) => {
    try {
      await axios.delete(`${API}/user/mt5-account/${accountId}`, { headers: getHeaders() });
      toast.success("MT5 account removed");
      await fetchAccounts();
    } catch (err) {
      toast.error(apiErrorDetail(err, "Failed to remove account"));
    }
  };

  const handleToggle = async (accountId) => {
    try {
      const res = await axios.put(`${API}/user/mt5-account/${accountId}/toggle`, {}, { headers: getHeaders() });
      setAccounts((prev) => prev.map((a) => (a.id === accountId ? { ...a, trading_enabled: res.data.trading_enabled } : a)));
      toast.success(res.data.trading_enabled ? "Auto-trading enabled" : "Auto-trading paused");
      onAccountsChange?.(accounts);
    } catch (err) {
      toast.error(apiErrorDetail(err, "Failed to toggle"));
    }
  };

  const handleSaveSettings = async (accountId, updates) => {
    try {
      await axios.put(`${API}/user/mt5-account/${accountId}/settings`, updates, { headers: getHeaders() });
      await fetchAccounts();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiErrorDetail(err, "Failed to save settings"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[#52525B]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading accounts...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {planLimits && (
        <div className="px-4 py-3 rounded-xl bg-[#0A0A0A] border border-[#27272A] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-xs font-medium text-white">
              Plan: {planMeta?.name || '—'} · Up to {planLimits.max_accounts} account{planLimits.max_accounts !== 1 ? 's' : ''}
            </p>
            <p className="text-[10px] text-[#52525B]">
              ${Number(planLimits.max_total_capital).toLocaleString()} total allocated capital
              {planMeta?.ai_credits_monthly ? ` · ${planMeta.ai_credits_monthly} AI credits/mo` : ''}
            </p>
          </div>
          <p className="text-sm font-data font-bold text-[#00E676]">
            ${Number(totalAllocated).toLocaleString()} / ${Number(planLimits.max_total_capital).toLocaleString()} used
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        {accounts.map((acct) => (
          <button
            key={acct.id}
            type="button"
            onClick={() => { setSelectedId(acct.id); setShowAddForm(false); }}
            className={`shrink-0 px-3 py-2 rounded-xl border text-xs font-medium transition-all ${
              selectedId === acct.id ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-white" : "bg-[#111111] border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]"
            }`}
          >
            {acct.account_number}
            {acct.trading_enabled && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-[#00E676] inline-block" />}
          </button>
        ))}
        {canAddAccount && (
          <button
            type="button"
            onClick={() => { setShowAddForm(true); setSelectedId(null); }}
            className="shrink-0 w-9 h-9 rounded-xl border border-dashed border-[#00E676]/40 text-[#00E676] flex items-center justify-center hover:bg-[#00E676]/10 transition-all"
            title="Add MT5 account"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {showAddForm && canAddAccount && (
        <Card className="bg-[#111111] border-[#00E676]/30">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#00E676]" /> Add MT5 Account
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <form onSubmit={handleLink} className="space-y-3">
              <p className="text-xs text-[#52525B]">
                Pro plan supports up to 3 accounts. Total allocated capital across all accounts cannot exceed ${Number(planLimits?.max_total_capital || 0).toLocaleString()}.
              </p>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Account Number</Label>
                <Input required type="text" inputMode="numeric" pattern="[0-9]*" placeholder="12345678" value={mt5Form.account_number}
                  onChange={(e) => setMt5Form({ ...mt5Form, account_number: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Password</Label>
                <div className="relative">
                  <Input required type={showPassword ? "text" : "password"} placeholder="Your MT5 password" value={mt5Form.password}
                    onChange={(e) => setMt5Form({ ...mt5Form, password: e.target.value })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1 pr-9" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white mt-0.5">
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Server Name</Label>
                <Input required placeholder="ICMarketsSC-Demo" value={mt5Form.server}
                  onChange={(e) => setMt5Form({ ...mt5Form, server: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={linking} className="flex-1 bg-[#00E676] hover:bg-[#00C853] text-black h-9 text-xs">
                  {linking ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Linking...</> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Link Account</>}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowAddForm(false)} className="text-[#A1A1AA] h-9 text-xs">Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {accounts.length === 0 && !showAddForm && (
        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm flex items-center gap-2"><Link2 className="w-4 h-4 text-[#007AFF]" /> MT5 Account</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <form onSubmit={handleLink} className="space-y-3">
              <p className="text-xs text-[#52525B]">Link your MT5 account to enable automated trading.</p>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Account Number</Label>
                <Input required type="text" inputMode="numeric" pattern="[0-9]*" placeholder="12345678" value={mt5Form.account_number}
                  onChange={(e) => setMt5Form({ ...mt5Form, account_number: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Password</Label>
                <div className="relative">
                  <Input required type={showPassword ? "text" : "password"} placeholder="Your MT5 password" value={mt5Form.password}
                    onChange={(e) => setMt5Form({ ...mt5Form, password: e.target.value })}
                    className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1 pr-9" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white mt-0.5">
                    {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#A1A1AA]">Server Name</Label>
                <Input required placeholder="ICMarketsSC-Demo" value={mt5Form.server}
                  onChange={(e) => setMt5Form({ ...mt5Form, server: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white h-9 mt-1" />
              </div>
              <Button type="submit" disabled={linking} className="w-full bg-[#007AFF] hover:bg-[#0062CC] text-white h-9 text-xs">
                {linking ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Linking...</> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Link Account</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {selected && !showAddForm && (
        <>
          <Card className="bg-[#111111] border-[#27272A]">
            <CardHeader className="pb-3 px-4 pt-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-[#007AFF]" /> Account {selected.account_number}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {statusBadge(selected.verification_status)}
                  <Button onClick={() => handleRemove(selected.id)} variant="ghost" size="sm" className="text-[#FF3D00] hover:bg-[#FF3D00]/10 h-7 text-xs">
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-4">
              <p className="text-xs text-[#52525B]">Server: {selected.server}</p>
              {selected.verification_status === "failed" && selected.verification_message && (
                <p className="text-xs text-[#FF3D00] bg-[#FF3D00]/5 px-3 py-2 rounded-lg">{selected.verification_message}</p>
              )}
              {selected.verification_status === "pending" && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-xs text-yellow-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                  {selected.verification_message || "Verification in progress — this may take a few hours."}
                </div>
              )}
            </CardContent>
          </Card>

          {selected.verification_status === "verified" && (
            <>
              <div className={`flex items-center justify-between gap-3 px-4 py-4 rounded-xl border ${selected.trading_enabled ? "bg-[#00E676]/5 border-[#00E676]/30" : "bg-[#111111] border-[#27272A]"}`}>
                <div className="flex items-center gap-3">
                  <Power className={`w-5 h-5 ${selected.trading_enabled ? "text-[#00E676]" : "text-[#52525B]"}`} />
                  <div>
                    <p className="text-sm font-semibold">{selected.trading_enabled ? "Auto-Trading Active" : "Auto-Trading Paused"}</p>
                    <p className="text-[11px] text-[#A1A1AA]">Account {selected.account_number}</p>
                  </div>
                </div>
                <Switch checked={selected.trading_enabled} onCheckedChange={() => handleToggle(selected.id)} />
              </div>

              <Card className="bg-[#111111] border-[#27272A]">
                <CardHeader className="pb-3 px-4 pt-4">
                  <CardTitle className="text-sm flex items-center gap-2"><Settings className="w-4 h-4 text-[#FFC107]" /> Risk Settings</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <AccountSettings
                    account={selected}
                    planLimits={planLimits}
                    totalAllocated={totalAllocated}
                    onSave={handleSaveSettings}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}

export { statusBadge };
